import { LoaderCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { operatorMessage } from '../shared/error-details';
import type { RuntimeComponentDownloadStatus } from '../shared/desktop-api';
import { RuntimeComponentDownload } from './RuntimeComponentDownload';
import { reportEvent, reportNotice, reportPlainNotice } from './telemetry';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';

/** `working`: a short action still under way (Downloading job setup…), shown with a turning mark
 * until it answers; a toast that arrives meanwhile is shown in its place. */
export const WorkspaceNoticeContext = createContext<{
  message: string;
  working?: string;
  action?: NoticeAction;
  dismiss: () => void;
}>({ message: '', dismiss: () => {} });

/** How long brief feedback stays before it goes on its own. */
export const NOTICE_SHOWN_MS = 8000;

/** The code names of a notice's way back, a fixed set (telemetry's no-user-text check lists them). */
export type NoticeActionName = 'document-delete-undo';
/**
 * The one way back a notice can carry ("Deleted an Invoice · Undo"): a link-style button after its sentence, gone
 * with the notice. `telemetry` names the button.
 */
export type NoticeAction = { label: string; run: () => void; telemetry: NoticeActionName };

/**
 * What telemetry sends for a toast instead of its words: a failure's or refusal's code (telemetry-catalog
 * `telemetryUiErrorCodes`), or `notice` for one that is not a failure whatever its words say ("Setup stopped" after
 * the person's own Stop). Without one, a toast is counted as `ui.toast` or `ui.error-toast` by its words.
 */
export type NoticeCode = TelemetryUiErrorCode | 'notice';
/** A toast publisher: the sentence to show, its code, and its way back if it has one. */
export type PublishNotice = (message: string, code?: NoticeCode, action?: NoticeAction) => void;

/** A completion belongs to the workspace visit that started it, including A/B/A. */
export function useWorkspaceNotice(
  scope: string,
): [string, PublishNotice, NoticeAction | undefined] {
  const visit = useMemo(() => ({}), [scope]);
  const currentVisit = useRef(visit);
  currentVisit.current = visit;
  const [notice, setNotice] = useState<{
    visit: object;
    message: string;
    action?: NoticeAction;
  } | null>(null);
  const publish = useCallback(
    (message: string, code?: NoticeCode, action?: NoticeAction) => {
      // A toast has room for the plain sentence only; technical details stay
      // with the screen that shows the error in full.
      if (currentVisit.current !== visit) return;
      const shown = operatorMessage(message);
      setNotice({ visit, message: shown, ...(shown && action ? { action } : {}) });
      // Which message it was, by its code; the words never leave this computer.
      if (!shown) return;
      if (code === 'notice') reportPlainNotice('toast');
      else if (code) reportEvent(`ui.error:${code}`, { attrs: { kind: 'toast' } });
      else reportNotice(shown);
    },
    [visit],
  );
  useEffect(() => {
    if (!notice?.message) return;
    const timer = window.setTimeout(
      () => setNotice((current) => (current === notice ? null : current)),
      NOTICE_SHOWN_MS,
    );
    return () => window.clearTimeout(timer);
  }, [notice]);
  const current = notice?.visit === visit ? notice : null;
  return [current?.message ?? '', publish, current?.action];
}

/** A message about a move the same step makes (deleting the open job, then landing on
 * another). Published straight away it would belong to the visit being left and never
 * show, so it waits until the move has rendered and belongs to where the operator lands. */
export function useNoticeAfterMove(publish: PublishNotice): PublishNotice {
  const [pending, setPending] = useState<{
    message: string;
    code?: NoticeCode;
    action?: NoticeAction;
  } | null>(null);
  useEffect(() => {
    if (!pending?.message) return;
    publish(pending.message, pending.code, pending.action);
    setPending(null);
  }, [pending, publish]);
  return useCallback(
    (message: string, code?: NoticeCode, action?: NoticeAction) =>
      setPending({ message, code, action }),
    [],
  );
}

/** For long work that moved the operator before it finished (a new job reading its
 * sample folder): publishes to the visit that is current when the work ends, not the
 * one it started in. The caller checks the operator is still on the job it is about. */
export function useNoticeForCurrentVisit(publish: PublishNotice): PublishNotice {
  const latest = useRef(publish);
  latest.current = publish;
  return useCallback(
    (message: string, code?: NoticeCode, action?: NoticeAction) =>
      latest.current(message, code, action),
    [],
  );
}

/**
 * Reserve real space for feedback, including when a Settings dialog is open.
 * Off the document screen it is also where the status indicators live (Learning,
 * This computer, Speed boost), in the same bottom-left place as the document bar.
 */
export function ApplicationStatusBar({
  componentDownload = null,
  indicators,
  downloadOnly = false,
}: {
  componentDownload?: RuntimeComponentDownloadStatus | null;
  indicators?: ReactNode;
  /** On the document screen: stands in for the document bar only while no document is open. */
  downloadOnly?: boolean;
} = {}) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bar = host.current;
    const shell = bar?.closest<HTMLElement>('.app-shell');
    if (!bar || !shell) return;
    // On the page root too: a dialog drawn at the root (the job setup window is) ends above
    // the bar as well, rather than running its bottom edge under it (seen October 3, 2026).
    const root = document.documentElement;
    const measure = () => {
      const height = `${bar.getBoundingClientRect().height}px`;
      shell.style.setProperty('--application-status-height', height);
      root.style.setProperty('--application-status-height', height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      shell.style.removeProperty('--application-status-height');
      root.style.removeProperty('--application-status-height');
    };
  }, []);
  return (
    <div
      ref={host}
      className={`application-status-bar${indicators ? ' has-indicators' : ''}${downloadOnly ? ' download-only' : ''}`}
    >
      {indicators}
      <WorkspaceNotice />
      <RuntimeComponentDownload status={componentDownload} />
    </div>
  );
}

/**
 * Ref for the review screen's document bar, which stands where the status bar would. It
 * publishes its height on the page root as --document-bar-height so the reminders in the
 * bottom-right corner clear it, also when a zoomed-in window wraps it onto two rows.
 */
export function trackDocumentBarHeight(bar: HTMLElement | null) {
  if (!bar) return;
  const root = bar.ownerDocument.documentElement;
  const measure = () =>
    root.style.setProperty('--document-bar-height', `${bar.getBoundingClientRect().height}px`);
  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(bar);
  return () => {
    observer.disconnect();
    root.style.removeProperty('--document-bar-height');
  };
}

/** Brief action feedback, never a dismissible notification stacked in the footer. */
export function WorkspaceNotice({
  fallbackMessage = '',
  onDismissFallback = () => {},
}: {
  fallbackMessage?: string;
  onDismissFallback?: () => void;
} = {}) {
  const notice = useContext(WorkspaceNoticeContext);
  const expireFallback = useRef(onDismissFallback);
  expireFallback.current = onDismissFallback;
  const previousLearning = useRef(fallbackMessage);
  useEffect(() => {
    if (fallbackMessage && previousLearning.current !== fallbackMessage) notice.dismiss();
    previousLearning.current = fallbackMessage;
  }, [fallbackMessage, notice.dismiss]);
  const working = !notice.message && Boolean(notice.working);
  const message = notice.message || notice.working || fallbackMessage;
  useEffect(() => {
    if (!fallbackMessage) return;
    const timer = window.setTimeout(() => expireFallback.current(), NOTICE_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [fallbackMessage]);
  if (!message) return null;
  const action = notice.message ? notice.action : undefined;
  return (
    <span
      className={`workspace-status-notice${working ? ' is-working' : ''}${action ? ' has-action' : ''}`}
      role="status"
    >
      {working && <LoaderCircle className="spin" size={14} aria-hidden="true" />}
      <span title={message}>{message}</span>
      {action && (
        <>
          <span className="workspace-status-notice-separator" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className="confirm-back"
            data-telemetry={action.telemetry}
            onClick={action.run}
          >
            {action.label}
          </button>
        </>
      )}
    </span>
  );
}
